import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

export default function ClausuradoTable() {
  const [clausurado, setClausurado] = useState([]);
  const [filteredClausurado, setFilteredClausurado] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchClausurado();
  }, []);

  useEffect(() => {
    filterAndSortClausurado();
  }, [searchTerm, clausurado, sortColumn,startDate,endDate, sortDirection]);

  const fetchClausurado = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/clausurados');
      setClausurado(response.data);
      setFilteredClausurado(response.data);
    } catch (err) {
      setError('Error al cargar los clausurados. Intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortClausurado = () => {
    let filtered = clausurado.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      const matchSearchTerm =
        item.observaciones.toLowerCase().includes(searchLower) ||
        item.sepulturaId.toString().includes(searchLower);
      const matchDateRange =
        (!startDate || new Date(item.fecha) >= new Date(startDate)) &&
        (!endDate || new Date(item.fecha) <= new Date(endDate));
      return matchSearchTerm && matchDateRange;
    });

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredClausurado(sorted);
    setCurrentPage(1);
  };

  const handleDeleteClausurado = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este clausurado?')) {
      setIsLoading(true);
      try {
        await axios.delete(`/clausurados/${id}`);
        fetchClausurado();
      } catch (err) {
        setError('Error al eliminar el clausurado. Intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredClausurado.slice(indexOfFirstItem, indexOfLastItem);
  
  const totalPages = Math.ceil(filteredClausurado.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center">Gestión de Clausurados</h1>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Clausurados</h2>
          <Link
            to="/home/clausurados/add"
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
          >
            <FaPlus className="mr-2" /> Añadir Clausurado
          </Link>
        </div>

        <div className="flex space-x-4 w-full">
          <input
            type="date"
            className="border p-2 rounded w-1/5"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            type="date"
            className="border p-2 rounded w-1/5"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="p-4 flex items-center bg-gray-100">
          <FaSearch className="mr-2" />
          <input
            type="text"
            placeholder="ID Sepultura o Observaciones"
            className="border p-2 rounded w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-500 text-white">
              <tr>
                {['id', 'fecha', 'observaciones', 'sepulturaId'].map((col) => (
                  <th
                    key={col}
                    className="px-6 py-3 cursor-pointer"
                    onClick={() => handleSort(col)}
                  >
                    {col.charAt(0).toUpperCase() + col.slice(1)}{' '}
                    {sortColumn === col &&
                      (sortDirection === 'asc' ? (
                        <FaSortAmountUp />
                      ) : (
                        <FaSortAmountDown />
                      ))}
                  </th>
                ))}
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{item.id}</td>
                  <td className="px-6 py-4">{item.fecha}</td>
                  <td className="px-6 py-4">{item.observaciones}</td>
                  <td className="px-6 py-4">{item.sepulturaId}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link
                      to={`/home/clausurados/${item.id}`}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <FaEye />
                    </Link>
                    <Link
                      to={`/home/clausurados/edit/${item.id}`}
                      className="text-green-600 hover:text-green-900"
                    >
                      <FaEdit />
                    </Link>
                    <button
                      onClick={() => handleDeleteClausurado(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center space-x-2 mt-4">
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => paginate(index + 1)}
              className={`px-4 py-2 border rounded ${
                currentPage === index + 1
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200'
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* CORRECTO
import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

export default function ClausuradoTable() {
  const [clausurado, setClausurado] = useState([]);
  const [filteredClausurado, setFilteredClausurado] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchClausurado();
  }, []);

  useEffect(() => {
    filterAndSortClausurado();
  }, [searchTerm, clausurado, sortColumn, sortDirection]);

  const fetchClausurado = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/clausurados');
      setClausurado(response.data);
      setFilteredClausurado(response.data);
    } catch (err) {
      setError('Error al cargar los clausurados. Intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortClausurado = () => {
    let filtered = clausurado.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.observaciones.toLowerCase().includes(searchLower) ||
        item.sepulturaId.toString().includes(searchLower)
      );
    });

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredClausurado(sorted);
    setCurrentPage(1);
  };

  const handleDeleteClausurado = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este clausurado?')) {
      setIsLoading(true);
      try {
        await axios.delete(`/clausurados/${id}`);
        fetchClausurado();
      } catch (err) {
        setError('Error al eliminar el clausurado. Intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredClausurado.slice(indexOfFirstItem, indexOfLastItem);
  
  const totalPages = Math.ceil(filteredClausurado.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center">Gestión de Clausurados</h1>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Clausurados</h2>
          <Link
            to="/home/clausurados/add"
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
          >
            <FaPlus className="mr-2" /> Añadir Clausurado
          </Link>
        </div>
        <div className="p-4 flex items-center bg-gray-100">
          <FaSearch className="mr-2" />
          <input
            type="text"
            placeholder="ID Sepultura o Observaciones"
            className="border p-2 rounded w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-500 text-white">
              <tr>
                {['id', 'fecha', 'observaciones', 'sepulturaId'].map((col) => (
                  <th
                    key={col}
                    className="px-6 py-3 cursor-pointer"
                    onClick={() => handleSort(col)}
                  >
                    {col.charAt(0).toUpperCase() + col.slice(1)}{' '}
                    {sortColumn === col &&
                      (sortDirection === 'asc' ? (
                        <FaSortAmountUp />
                      ) : (
                        <FaSortAmountDown />
                      ))}
                  </th>
                ))}
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{item.id}</td>
                  <td className="px-6 py-4">{item.fecha}</td>
                  <td className="px-6 py-4">{item.observaciones}</td>
                  <td className="px-6 py-4">{item.sepulturaId}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link
                      to={`/home/clausurados/${item.id}`}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <FaEye />
                    </Link>
                    <Link
                      to={`/home/clausurados/edit/${item.id}`}
                      className="text-green-600 hover:text-green-900"
                    >
                      <FaEdit />
                    </Link>
                    <button
                      onClick={() => handleDeleteClausurado(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center space-x-2 mt-4">
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => paginate(index + 1)}
              className={`px-4 py-2 border rounded ${
                currentPage === index + 1
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200'
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
*/

/*
import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

export default function ClausuradoTable() {
  const [clausurado, setClausurado] = useState([]);
  const [filteredClausurado, setFilteredClausurado] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchClausurado();
  }, []);

  useEffect(() => {
    filterAndSortClausurado();
  }, [searchTerm, clausurado, sortColumn, sortDirection]);

  const fetchClausurado = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/clausurados');
      setClausurado(response.data);
      setFilteredClausurado(response.data);
    } catch (err) {
      setError('Error al cargar los clausurados. Intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortClausurado = () => {
    let filtered = clausurado.filter(item =>
      item.observaciones.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredClausurado(sorted);
    setCurrentPage(1);
  };

  const handleDeleteClausurado = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este clausurado?')) {
      setIsLoading(true);
      try {
        await axios.delete(`/clausurados/${id}`);
        fetchClausurado();
      } catch (err) {
        setError('Error al eliminar el clausurado. Intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredClausurado.slice(indexOfFirstItem, indexOfLastItem);
  
  const totalPages = Math.ceil(filteredClausurado.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center">Gestión de Clausurados</h1>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Clausurados</h2>
          <Link
            to="/home/clausurados/add"
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
          >
            <FaPlus className="mr-2" /> Añadir Clausurado
          </Link>
        </div>
        <div className="p-4 flex items-center bg-gray-100">
          <FaSearch className="mr-2" />
          <input
            type="text"
            placeholder="Buscar por Observaciones"
            className="border p-2 rounded w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-500 text-white">
              <tr>
                {['id', 'fecha', 'observaciones', 'sepulturaId'].map((col) => (
                  <th
                    key={col}
                    className="px-6 py-3 cursor-pointer"
                    onClick={() => handleSort(col)}
                  >
                    {col.charAt(0).toUpperCase() + col.slice(1)}{' '}
                    {sortColumn === col &&
                      (sortDirection === 'asc' ? (
                        <FaSortAmountUp />
                      ) : (
                        <FaSortAmountDown />
                      ))}
                  </th>
                ))}
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{item.id}</td>
                  <td className="px-6 py-4">{item.fecha}</td>
                  <td className="px-6 py-4">{item.observaciones}</td>
                  <td className="px-6 py-4">{item.sepulturaId}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link
                      to={`/home/clausurados/${item.id}`}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <FaEye />
                    </Link>
                    <Link
                      to={`/home/clausurados/edit/${item.id}`}
                      className="text-green-600 hover:text-green-900"
                    >
                      <FaEdit />
                    </Link>
                    <button
                      onClick={() => handleDeleteClausurado(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center space-x-2 mt-4">
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => paginate(index + 1)}
              className={`px-4 py-2 border rounded ${
                currentPage === index + 1
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200'
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}  
*/

  /*
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredClausurado.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredClausurado.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center">Gestión de Clausurados</h1>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Clausurados</h2>
          <Link to="/home/clausurados/add" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
            <FaPlus className="mr-2" /> Añadir Clausurado
          </Link>
        </div>
        <div className="p-4 flex items-center bg-gray-100">
          <FaSearch className="mr-2" />
          <input
            type="text"
            placeholder="Buscar por Observaciones"
            className="border p-2 rounded w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {['id', 'fecha', 'observaciones', 'sepulturaId'].map(col => (
                  <th key={col} className="px-6 py-3 cursor-pointer" onClick={() => handleSort(col)}>
                    {col.charAt(0).toUpperCase() + col.slice(1)} {sortColumn === col && (sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}
                  </th>
                ))}
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{item.id}</td>
                  <td className="px-6 py-4">{item.fecha}</td>
                  <td className="px-6 py-4">{item.observaciones}</td>
                  <td className="px-6 py-4">{item.sepulturaId}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link to={`/home/clausurados/${item.id}`} className="text-blue-600 hover:text-blue-900"><FaEye /></Link>
                    <Link to={`/home/clausurados/edit/${item.id}`} className="text-green-600 hover:text-green-900"><FaEdit /></Link>
                    <button onClick={() => handleDeleteClausurado(item.id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center space-x-2 mt-4">
          {[...Array(totalPages)].map((_, index) => (
            <button key={index} onClick={() => paginate(index + 1)} className={`px-4 py-2 border rounded ${currentPage === index + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>{index + 1}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
*/

/*
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const ClausuradoList = () => {
  const [clausurados, setClausurados] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredClausurados, setFilteredClausurados] = useState([]);
  const [editClausurado, setEditClausurado] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const [newClausurado, setNewClausurado] = useState({
    fecha: '',
    observaciones: '',
    sepulturaId: ''
  });

  useEffect(() => {
    axios.get('http://localhost:3000/clausurados')
      .then(response => {
        setClausurados(response.data);
        setFilteredClausurados(response.data);
      })
      .catch(error => {
        console.error('Error fetching clausurados:', error);
      });
  }, []);

  useEffect(() => {
    setFilteredClausurados(
      clausurados.filter(clausurado =>
        (clausurado.sepulturaId ? clausurado.sepulturaId.toString().toLowerCase() : '').includes(search.toLowerCase())
      )
    );
    setCurrentPage(1);
  }, [search, clausurados]);

  const handleEdit = (clausurado) => {
    setEditClausurado(clausurado);
    setNewClausurado(clausurado);
  };

  const handleSave = () => {
    axios.put(`http://localhost:3000/clausurados/${editClausurado.id}`, newClausurado)
      .then(response => {
        setClausurados(clausurados.map(c => c.id === editClausurado.id ? response.data : c));
        setFilteredClausurados(filteredClausurados.map(c => c.id === editClausurado.id ? response.data : c));
        setEditClausurado(null);
      })
      .catch(error => {
        console.error('Error updating clausurado:', error);
      });
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este clausurado?')) {
      axios.delete(`http://localhost:3000/clausurados/${id}`)
        .then(() => {
          setClausurados(clausurados.filter(c => c.id !== id));
          setFilteredClausurados(filteredClausurados.filter(c => c.id !== id));
        })
        .catch(error => {
          console.error('Error deleting clausurado:', error);
        });
    }
  };

  const handleAdd = () => {
    const { sepulturaId, ...data } = newClausurado;

    if (!sepulturaId) {
      alert('Debe ingresar un ID de sepultura antes de grabar.');
      return;
    }

    axios.post(`http://localhost:3000/clausurados/${sepulturaId}`, data)
      .then(response => {
        setClausurados([...clausurados, response.data]);
        setFilteredClausurados([...filteredClausurados, response.data]);
        setNewClausurado({ fecha: '', observaciones: '', sepulturaId: '' });
      })
      .catch(error => {
        console.error('Error adding clausurado:', error);
      });
  };

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredClausurados.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredClausurados.length / recordsPerPage);

  return (
    <div style={{ padding: '20px' }}>
      <h1>Lista de Clausurados</h1>
      <input
        type="text"
        placeholder="Buscar por ID de Sepultura"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: '20px', padding: '10px', width: '100%' }}
      />

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th>ID</th><th>Fecha</th><th>Observaciones</th><th>ID Sepultura</th><th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {currentRecords.map(clausurado => (
            <tr key={clausurado.id}>
              <td>{clausurado.id}</td>
              <td>{clausurado.fecha}</td>
              <td>{clausurado.observaciones}</td>
              <td>{clausurado.sepulturaId}</td>
              <td>
                <button onClick={() => handleEdit(clausurado)}>✏️</button>
                <button onClick={() => handleDelete(clausurado.id)}>🗑️</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div>
        <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>◀ Anterior</button>
        <span> Página {currentPage} de {totalPages} </span>
        <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>Siguiente ▶</button>
      </div>

      <h2>Añadir Nuevo Clausurado</h2>
      {Object.keys(newClausurado).map(key => (
        <input
          key={key}
          type="text"
          placeholder={key}
          value={newClausurado[key]}
          onChange={e => setNewClausurado({ ...newClausurado, [key]: e.target.value })}
          style={{ margin: '5px', padding: '10px' }}
        />
      ))}
      <button onClick={handleAdd} style={{ padding: '10px', backgroundColor: 'green', color: 'white' }}>➕ Grabar</button>
    </div>
  );
};

export default ClausuradoList;
*/